// src/components/stocks/StockForm.js
import React, { useState, useEffect, useCallback, useRef } from "react";
import { Search, ChevronDown } from "lucide-react";
import { productAPI } from "../../services";
import { useSelector } from "react-redux";

const StockForm = ({
  onSubmit,
  onCancel,
  editingStock,
  products = [],
  allBatches = [],
}) => {
  const [availableProducts, setAvailableProducts] = useState(products);
  const [isLoadingProducts, setIsLoadingProducts] = useState(false);
  const [selectedProduct, setSelectedProduct] = useState(null);
  const searchInputRef = useRef(null);

  const [formData, setFormData] = useState({
    productId: "",
    buyingPrice: "",
    sellingPrice: "",
    quantity: "",
    uniqueId: "",
  });

  const [errors, setErrors] = useState({});
  const [isProductDropdownOpen, setIsProductDropdownOpen] = useState(false);
  const [productSearchTerm, setProductSearchTerm] = useState("");
  const { isPartsURL, isAccessoriesURL } = useSelector((state) => state.domain);

  // Search products API call
  const searchProducts = useCallback(
    async (searchTerm) => {
      if (!searchTerm.trim()) {
        setAvailableProducts(products);
        return;
      }

      setIsLoadingProducts(true);
      try {
        const response = await productAPI.getProductsList({
          search: searchTerm,
          page: 1,
          page_size: 50,
        });
        setAvailableProducts(response.results || []);
      } catch (error) {
        console.error("Error searching products:", error);
        setAvailableProducts(products);
      } finally {
        setIsLoadingProducts(false);
      }
    },
    [products]
  );

  // Debounced search effect
  useEffect(() => {
    const timeoutId = setTimeout(() => {
      searchProducts(productSearchTerm);
    }, 300);

    return () => clearTimeout(timeoutId);
  }, [productSearchTerm, searchProducts]);

  useEffect(() => {
    if (editingStock) {
      // Handle both purchase API structure and legacy structure
      const productId = editingStock.product || editingStock.productId || "";
      const newFormData = {
        productId: productId,
        buyingPrice:
          editingStock.buying_price || editingStock.buyingPrice || "",
        sellingPrice:
          editingStock.selling_price || editingStock.sellingPrice || "",
        quantity: editingStock.initial_quantity || editingStock.quantity || "",
        uniqueId: editingStock.unique_id || editingStock.uniqueId || "",
      };

      setFormData(newFormData);

      // Find and set the selected product
      if (productId) {
        const product =
          products.find((p) => p.id === productId) ||
          editingStock.product_detail;
        if (product) {
          setSelectedProduct(product);
        }
      }
    } else {
      // Clear all fields for new stock
      setFormData({
        productId: "",
        buyingPrice: "",
        sellingPrice: "",
        quantity: "",
        uniqueId: "",
      });
      setSelectedProduct(null);
    }
  }, [editingStock, products]);

  const validateForm = () => {
    const newErrors = {};
    if (!formData.productId) newErrors.productId = "Product is required";
    if (!formData.buyingPrice || parseFloat(formData.buyingPrice) <= 0)
      newErrors.buyingPrice = "Buy price must be greater than 0";
    if (!formData.sellingPrice || parseFloat(formData.sellingPrice) <= 0)
      newErrors.sellingPrice = "Sell price must be greater than 0";
    if (!formData.quantity || parseInt(formData.quantity) <= 0)
      newErrors.quantity = "Quantity must be greater than 0";

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData((prev) => ({ ...prev, [name]: value }));

    if (errors[name]) {
      setErrors((prev) => ({ ...prev, [name]: "" }));
    }
  };

  const handleSubmit = (e) => {
    e.preventDefault();
    if (!validateForm()) return;

    const buyingPrice = parseFloat(formData.buyingPrice);
    const quantity = parseInt(formData.quantity);
    const totalAmount = buyingPrice * quantity;

    const stockData = {
      productId: formData.productId,
      buyingPrice: buyingPrice,
      sellingPrice: parseFloat(formData.sellingPrice),
      quantity: quantity,
      totalAmount: totalAmount,
      id: editingStock ? editingStock.id : Date.now().toString(),
      updatedAt: editingStock ? new Date().toISOString() : undefined,
      createdAt: editingStock
        ? editingStock.createdAt
        : new Date().toISOString(),
    };

    // Only include uniqueId if it has a value
    if (formData.uniqueId && formData.uniqueId.trim() !== "") {
      stockData.uniqueId = formData.uniqueId.trim();
    }

    onSubmit(stockData);
  };

  const handleProductSelect = useCallback((product) => {
    setFormData((prev) => ({
      ...prev,
      productId: product.id,
    }));
    setSelectedProduct(product);
    setIsProductDropdownOpen(false);
    setProductSearchTerm("");

    // Clear error when user selects a product
    setErrors((prev) => {
      if (prev.productId) {
        const newErrors = { ...prev };
        delete newErrors.productId;
        return newErrors;
      }
      return prev;
    });
  }, []);

  const filteredProducts = availableProducts;

  // Close dropdown when clicking outside and auto-focus search input
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (
        isProductDropdownOpen &&
        !event.target.closest(".stock-product-dropdown-container")
      ) {
        setIsProductDropdownOpen(false);
      }
    };

    if (isProductDropdownOpen) {
      document.addEventListener("mousedown", handleClickOutside);
      // Auto-focus search input when dropdown opens
      setTimeout(() => {
        searchInputRef.current?.focus();
      }, 0);
    }

    return () => {
      document.removeEventListener("mousedown", handleClickOutside);
    };
  }, [isProductDropdownOpen]);

  return (
    <div className="stock-modal-overlay">
      <div className="stock-modal noscrollbar">
        <div className="stock-modal-header">
          <div className="stock-modal-title-section">
            <h2 className="stock-modal-title">
              {editingStock ? "Edit Stock Item" : "Add Stock to Batch"}
            </h2>
            <p className="stock-modal-subtitle">
              {editingStock
                ? "Update stock item pricing and quantity information."
                : "Add stock items to this batch with pricing and quantity information."}
            </p>
          </div>
        </div>

        <form onSubmit={handleSubmit} className="stock-modal-form">
          <div className="stock-modal-body">
            {/* Product Selection */}
            <div className="stock-form-group">
              <label className="stock-form-label">Product *</label>
              <div className="stock-product-dropdown-container">
                <div
                  className={`${isPartsURL ? "dropdown-input-parts" : isAccessoriesURL ? "dropdown-input-acc" : "dropdown-input"} ${
                    errors.productId ? "is-invalid" : ""
                  } ${editingStock ? "disabled" : ""}`}
                  onClick={() => {
                    if (!editingStock)
                      setIsProductDropdownOpen(!isProductDropdownOpen);
                  }}
                  style={
                    editingStock ? { cursor: "not-allowed", opacity: 0.6 } : {}
                  }
                >
                  <span
                    className={
                      selectedProduct ? "selected-value" : "not-selected-value"
                    }
                  >
                    {selectedProduct
                      ? `${
                          selectedProduct.brand_name ||
                          selectedProduct.brandName ||
                          ""
                        } ${
                          selectedProduct.name ||
                          selectedProduct.modelName ||
                          ""
                        }`
                      : "Select product"}
                  </span>
                  <ChevronDown className="dropdown-arrow" size={16} />
                </div>

                {isProductDropdownOpen && (
                  <div
                    className="dropdown-menu"
                    style={{
                      zIndex: 9999,
                      position: "absolute",
                      top: "100%",
                      left: 0,
                      right: 0,
                      backgroundColor: "white",
                      border: "1px solid #d1d5db",
                      borderRadius: "8px",
                      boxShadow: "0 4px 6px -1px rgba(0, 0, 0, 0.1)",
                      marginTop: "4px",
                    }}
                  >
                    <div className="dropdown-search">
                      <Search className="batches-search-icon" size={16} />
                      <input
                        ref={searchInputRef}
                        type="text"
                        className={`${isPartsURL ? "batches-search-input-parts" : isAccessoriesURL ? "batches-search-input-acc" : "batches-search-input"}`}
                        placeholder="Search product name..."
                        value={productSearchTerm}
                        onChange={(e) => setProductSearchTerm(e.target.value)}
                        onClick={(e) => e.stopPropagation()}
                      />
                    </div>
                    <div className="dropdown-list noscrollbar">
                      {isLoadingProducts ? (
                        <div className="batches-dropdown-item no-results">
                          Loading products...
                        </div>
                      ) : filteredProducts.length > 0 ? (
                        filteredProducts.map((product) => {
                          const brandName =
                            product.brand_name || product.brandName || "";
                          const modelName =
                            product.name || product.modelName || "";
                          return (
                            <div
                              key={product.id}
                              className="batches-dropdown-item"
                              onClick={() => {
                                handleProductSelect(product);
                              }}
                            >
                              <div>
                                <div>
                                  {brandName} {modelName}
                                </div>
                                <div
                                  style={{
                                    fontSize: "0.8em",
                                    color: "#666",
                                    marginTop: "4px",
                                  }}
                                >
                                  {product.ram && `${product.ram}`}
                                  {product.ram && product.storage && " | "}
                                  {product.storage && `${product.storage}`}
                                  {product.variants &&
                                    product.variants.length > 0 && (
                                      <>
                                        {(product.ram || product.storage) &&
                                          " | "}
                                        {product.variants
                                          .map((variant) =>
                                            Object.values(variant).join(", ")
                                          )
                                          .join(", ")}
                                      </>
                                    )}
                                  {product.suffix && (
                                    <>
                                      {(product.ram ||
                                        product.storage ||
                                        (product.variants &&
                                          product.variants.length > 0)) &&
                                        " | "}
                                      {product.suffix}
                                    </>
                                  )}
                                  {product.used_mobile !== undefined &&
                                    product.used_mobile !== null && (
                                      <>
                                        {(product.ram ||
                                          product.storage ||
                                          (product.variants &&
                                            product.variants.length > 0) ||
                                          product.suffix) &&
                                          " | "}
                                        {product.used_mobile === false
                                          ? "New"
                                          : "Used"}
                                      </>
                                    )}
                                </div>
                              </div>
                            </div>
                          );
                        })
                      ) : (
                        <div className="batches-dropdown-item no-results">
                          No products found
                        </div>
                      )}
                    </div>
                  </div>
                )}
              </div>
              {errors.productId && (
                <div className="stock-form-error">{errors.productId}</div>
              )}
            </div>

            {/* Buy Price and Sell Price on same line */}
            <div className="stock-form-row">
              <div className="stock-form-group">
                <label className="stock-form-label">Buy Price *</label>
                <input
                  type="number"
                  name="buyingPrice"
                  className={`${isPartsURL ? "stock-form-input-parts" : isAccessoriesURL ? "stock-form-input-acc" : "stock-form-input"} ${
                    errors.buyingPrice ? "error" : ""
                  }`}
                  value={formData.buyingPrice}
                  onChange={handleChange}
                  step="0.01"
                  min="0"
                />
                {errors.buyingPrice && (
                  <div className="stock-form-error">{errors.buyingPrice}</div>
                )}
              </div>

              <div className="stock-form-group">
                <label className="stock-form-label">Sell Price *</label>
                <input
                  type="number"
                  name="sellingPrice"
                  className={`${isPartsURL ? "stock-form-input-parts" : isAccessoriesURL ? "stock-form-input-acc" : "stock-form-input"} ${
                    errors.sellingPrice ? "error" : ""
                  }`}
                  value={formData.sellingPrice}
                  onChange={handleChange}
                  step="0.01"
                  min="0"
                />
                {errors.sellingPrice && (
                  <div className="stock-form-error">{errors.sellingPrice}</div>
                )}
              </div>
            </div>

            {/* Quantity on new line */}
            <div className="stock-form-group">
              <label className="stock-form-label">Quantity *</label>
              <input
                type="number"
                name="quantity"
                className={`${isPartsURL ? "stock-form-input-parts" : isAccessoriesURL ? "stock-form-input-acc" : "stock-form-input"} ${errors.quantity ? "error" : ""}`}
                value={formData.quantity}
                onChange={handleChange}
                min="1"
                disabled={editingStock}
                style={
                  editingStock ? { cursor: "not-allowed", opacity: 0.6 } : {}
                }
              />
              {errors.quantity && (
                <div className="stock-form-error">{errors.quantity}</div>
              )}
            </div>

            {/* Unique ID Field */}
            <div className="stock-form-group">
              <label className="stock-form-label">Unique ID</label>
              <input
                type="text"
                name="uniqueId"
                className={`${isPartsURL ? "stock-form-input-parts" : isAccessoriesURL ? "stock-form-input-acc" : "stock-form-input"}`}
                value={formData.uniqueId}
                onChange={handleChange}
                placeholder="Serial number or unique number"
              />
            </div>
          </div>

          <div className="stock-modal-footer">
            <button
              type="button"
              className="stock-cancel-btn"
              onClick={onCancel}
            >
              Cancel
            </button>
            <button type="submit" className={`${isPartsURL ? "stock-submit-btn-parts" : isAccessoriesURL ? "stock-submit-btn-acc" : "stock-submit-btn"}`}>
              {editingStock ? "Update Stock" : "Add Stock"}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
};

export default StockForm;
